import { llmRequest } from "./client";
import { CONSOLIDATION_SYSTEM_PROMPT } from "./prompts";
import type { TailoredCVContent } from "./document-gen";

export interface ConsolidationSource {
  vacancy_title: string;
  vacancy_id: string;
  generated_at: string;
  content: TailoredCVContent;
}

export interface ConsolidationResult {
  system_master: TailoredCVContent;
  source_count: number;
}

const roleSchema = {
  type: "object",
  properties: {
    title:        { type: "string" },
    organisation: { type: "string" },
    period:       { type: "string" },
    description:  { type: "string" },
    bullets:      { type: "array", items: { type: "string" } },
    result:       { type: "string" },
    tech:         { type: "string" },
  },
  required: ["title", "organisation", "period", "description", "bullets", "result", "tech"],
};

const educationSchema = {
  type: "object",
  properties: {
    degree:      { type: "string" },
    institution: { type: "string" },
    year:        { type: "string" },
  },
  required: ["degree", "institution", "year"],
};

const consolidationSchema = {
  type: "object",
  properties: {
    document_type:           { type: "string", enum: ["tailored_cv"] },
    language:                { type: "string" },
    role_title:              { type: "string" },
    profile_text:            { type: "string" },
    competencies:            { type: "array", items: { type: "string" } },
    tech_stack: {
      type: "array",
      items: {
        type: "object",
        properties: {
          category: { type: "string" },
          technologies: { type: "string" },
        },
        required: ["category", "technologies"],
      },
    },
    roles:                   { type: "array", items: roleSchema },
    education:               { type: "array", items: educationSchema },
    certifications:          { type: "array", items: { type: "string" } },
    tailoring_notes:         { type: "string" },
    known_gaps_not_resolved: { type: "array", items: { type: "string" } },
  },
  required: [
    "document_type", "language", "role_title", "profile_text",
    "competencies", "tech_stack", "roles", "education",
    "certifications", "tailoring_notes", "known_gaps_not_resolved",
  ],
};

export async function consolidateProfile(
  _apiKey: string,   // kept for call-site compat during transition; ignored
  _model: string,
  humanCvText: string,
  profileJson: string,
  sources: ConsolidationSource[],
  currentSystemMaster: TailoredCVContent | null,
  systemPrompt?: string | null,
): Promise<ConsolidationResult> {
  const sourcesBlock = sources
    .map((s, i) => `### Source ${i + 1}: ${s.vacancy_title}\n${JSON.stringify(s.content, null, 2)}`)
    .join("\n\n");

  const userPrompt =
    `Human Master CV (plain text — the factual baseline):\n${humanCvText}\n\n` +
    `Consultant Profile (JSON — confirmed skills, gaps, inferences):\n${profileJson}\n\n` +
    (currentSystemMaster
      ? `Current System Master CV (JSON — improve on this):\n${JSON.stringify(currentSystemMaster, null, 2)}\n\n`
      : "") +
    `Tailored CVs to consolidate (${sources.length} source${sources.length !== 1 ? "s" : ""}):\n${sourcesBlock}\n\n` +
    `Produce the consolidated System Master CV in Dutch.`;

  const raw = await llmRequest<any>({
    systemPrompt: systemPrompt || CONSOLIDATION_SYSTEM_PROMPT,
    userContent: [{ type: "text", text: userPrompt }],
    schema: consolidationSchema,
    maxTokens: 10000,
    temperature: 0.2,
  });

  return { system_master: parseAndReconstructCv(raw), source_count: sources.length };
}

function parseAndReconstructCv(parsed: any): TailoredCVContent {
  if (Array.isArray(parsed.tech_stack)) {
    const record: Record<string, string> = {};
    for (const item of parsed.tech_stack) {
      if (item && typeof item === "object" && item.category) {
        record[item.category] = item.technologies || "";
      }
    }
    parsed.tech_stack = record;
  }
  return parsed as TailoredCVContent;
}

export function computeConsolidationDiff(
  prev: TailoredCVContent | null,
  next: TailoredCVContent,
): { newCompetencies: number; newRoles: number; improvedProfile: boolean } {
  if (!prev) {
    return { newCompetencies: next.competencies.length, newRoles: next.roles.length, improvedProfile: true };
  }
  const prevCompSet = new Set(prev.competencies.map((c) => c.toLowerCase()));
  const newCompetencies = next.competencies.filter((c) => !prevCompSet.has(c.toLowerCase())).length;
  const prevRoleKeys = new Set(prev.roles.map((r) => `${r.title}|${r.organisation}`));
  const newRoles = next.roles.filter((r) => !prevRoleKeys.has(`${r.title}|${r.organisation}`)).length;
  const improvedProfile = next.profile_text !== prev.profile_text;
  return { newCompetencies, newRoles, improvedProfile };
}
